<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import DOMPurify from 'dompurify'
import { useAiChatStore, type ChatMessage, type ToolActivity, type MessageSegment } from '@/stores/ai-chat'
import { aiApi } from '@/api/ai'
import {
  CollapsibleRoot as Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'reka-ui'
import {
  AtSign,
  ChevronRight,
  Brain,
  Check,
  GitCompareArrows,
  Search,
  Database,
  FileText,
  ShieldCheck,
  Loader2,
  Wrench,
  Filter,
  Table,
  Play,
  BookOpen,
  Globe,
  Navigation,
  History,
  Clock,
  AlertTriangle,
  RotateCcw,
} from 'lucide-vue-next'
import { Marked } from 'marked'
import { dedupeResourceRefs, isInternalAppPath, resourceRefHasRoute, resourceRefLabel, resourceRefToPath, type ChatReference } from '@/lib/resource-links'
import HoverCard from '@/components/ui/HoverCard.vue'
import ResourcePreview from '@/components/ai/previews/ResourcePreview.vue'
import YamlDiffPreview from '@/components/ai/previews/YamlDiffPreview.vue'
import { Badge } from '@kyoto2/components/ui/badge'
import { Button } from '@kyoto2/components/ui/button'
import { useRouter } from 'vue-router'
import { useRouteContext } from '@/composables/useRouteContext'

const store = useAiChatStore()
const router = useRouter()
const routeContext = useRouteContext()
const container = ref<HTMLElement | null>(null)

// A persistent "typing" indicator so the panel never looks frozen while the AI
// works. It defers to existing cues — streaming text is its own progress signal
// and an in-flight tool call renders a spinner row — and shows during every
// other waiting phase: initial latency, thinking, and (the case that looked
// stuck) the silent gap after a tool result before the model's next turn.
const showTypingIndicator = computed(() => {
  if (!store.isStreaming) return false
  const msg = store.messages[store.messages.length - 1]
  if (!msg || msg.role !== 'assistant') return false
  const segs = (msg.segments ?? []) as MessageSegment[]
  const last = segs[segs.length - 1]
  if (last?.type === 'text' && segmentHasVisibleText(last.text)) return false
  if (last?.type === 'tool_call' && last.activity.status === 'calling') return false
  return true
})

// Follow the stream to the bottom, but stop following once the user scrolls
// up to read earlier content (and resume when they scroll back near bottom).
let autoFollow = true

function isNearBottom(): boolean {
  const el = container.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < 120
}

function onScroll() {
  autoFollow = isNearBottom()
}

function scrollToBottom(force = false) {
  const el = container.value
  if (!el || (!force && !autoFollow)) return
  el.scrollTop = el.scrollHeight
}

// New message (user send or assistant placeholder) → always snap to bottom.
watch(() => store.messages.length, () => {
  autoFollow = true
  nextTick(() => scrollToBottom(true))
})
// Streaming deltas → follow only if the user hasn't scrolled away.
watch(() => store.currentThinking, () => nextTick(() => scrollToBottom()))
watch(() => store.currentText, () => nextTick(() => scrollToBottom()))
// The post-tool-result gap changes neither text nor thinking, so follow the
// indicator itself into view when it appears.
watch(showTypingIndicator, v => { if (v) nextTick(() => scrollToBottom()) })

// Own instance — never mutate the global `marked` singleton (breaks: true would
// leak into every other marked user, e.g. dashboard callouts).
const chatMarked = new Marked({ breaks: true, gfm: true })

function renderMarkdown(text: string): string {
  // When the editor is bound, the YAML is streamed into it and shown as a diff
  // preview, so we strip it from the chat bubble. Otherwise (global sidebar)
  // there is no editor, so the YAML must stay visible as a normal code block.
  const source = store.editorActive ? store.stripYamlBlocks(text) : text
  const trimmed = source.trim()
  if (!trimmed) return ''
  return DOMPurify.sanitize(chatMarked.parse(trimmed) as string)
}

function segmentHasVisibleText(text: string): boolean {
  const source = store.editorActive ? store.stripYamlBlocks(text) : text
  return !!source.trim()
}

function segmentKey(msg: ChatMessage, si: number, seg: MessageSegment): string {
  if (seg.type === 'tool_call') {
    return `${msg.id}-${si}-tool-${seg.activity.toolUseId ?? seg.activity.name}`
  }
  if (seg.type === 'resource') {
    return `${msg.id}-${si}-resource-${seg.refs.map(r => `${r.type}:${r.id}`).join(',')}`
  }
  return `${msg.id}-${si}-text`
}

function onMarkdownClick(e: MouseEvent) {
  const target = e.target as HTMLElement
  const anchor = target.closest('a')
  if (!anchor) return
  const href = anchor.getAttribute('href')
  if (href && isInternalAppPath(href)) {
    e.preventDefault()
    router.push(href)
  }
}

function formatAttachmentSize(bytes: number) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatThinkingDuration(ms?: number) {
  if (!ms) return ''
  const s = Math.round(ms / 1000)
  return s < 1 ? '<1s' : `${s}s`
}

function yamlStatusLabel(msg: ChatMessage) {
  // Editor-status chips ("Applied to editor", etc.) only make sense when an
  // editor is actually attached.
  if (!store.editorActive) return null
  if (store.taskEditorPhase === 'streaming') return 'Streaming YAML to editor'
  if (!store.extractYamlBlocks(msg.text).length) return null
  if (store.taskEditorPhase === 'review') return 'Review changes in editor'
  return 'Applied to editor'
}

function yamlBlocksForMessage(msg: ChatMessage): string[] {
  const fromText = store.extractYamlBlocks(msg.text)
  if (fromText.length) return fromText
  return msg.segments
    ?.filter((s): s is { type: 'text'; text: string } => s.type === 'text')
    .flatMap(s => store.extractYamlBlocks(s.text)) ?? []
}

const TOOL_ICONS: Record<string, typeof Search> = {
  list_connectors: Search,
  list_connector_resources: Database,
  search_connector_resources: Filter,
  describe_connector_resource: Table,
  query_connector: Play,
  list_connector_methods: Wrench,
  list_tasks: FileText,
  read_task: FileText,
  read_task_versions: History,
  list_reference_docs: BookOpen,
  read_reference_doc: BookOpen,
  validate_yaml: ShieldCheck,
  validate_dashboard_yaml: ShieldCheck,
  list_api_operations: Globe,
  describe_api_operation: Globe,
  search_app: Search,
  call_api: Globe,
  navigate: Navigation,
  wait: Clock,
}

const TOOL_LABELS: Record<string, string> = {
  list_connectors: 'Listed connectors',
  list_connector_resources: 'Listed resources',
  search_connector_resources: 'Searched resources',
  describe_connector_resource: 'Described resource',
  query_connector: 'Sampled data',
  list_connector_methods: 'Fetched methods',
  list_tasks: 'Listed tasks',
  read_task: 'Read task',
  read_task_versions: 'Read task history',
  list_reference_docs: 'Listed docs',
  read_reference_doc: 'Read doc',
  validate_yaml: 'Validated YAML',
  validate_dashboard_yaml: 'Validated dashboard YAML',
  list_api_operations: 'Listed API operations',
  describe_api_operation: 'Described API operation',
  search_app: 'Searched app',
  call_api: 'API call',
  navigate: 'Navigate',
  wait: 'Waiting',
}

const now = ref(Date.now())
let tickTimer: ReturnType<typeof setInterval> | null = null

const hasCallingTool = computed(() =>
  store.messages.some(m =>
    m.toolCalls?.some(t => t.status === 'calling')
    || m.segments?.some(s => s.type === 'tool_call' && s.activity.status === 'calling'),
  ),
)

watch(hasCallingTool, active => {
  if (active && !tickTimer) {
    now.value = Date.now()
    tickTimer = setInterval(() => { now.value = Date.now() }, 1000)
  } else if (!active && tickTimer) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}, { immediate: true })

onUnmounted(() => {
  if (tickTimer) clearInterval(tickTimer)
})

function formatElapsedMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`
}

function formatCapMs(ms: unknown): string | null {
  const n = typeof ms === 'number' ? ms : typeof ms === 'string' ? Number(ms) : NaN
  if (!Number.isFinite(n) || n <= 0) return null
  return formatElapsedMs(n)
}

function targetId(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return null
}

function waitCallingLabel(tc: ToolActivity): string | null {
  if (tc.name !== 'wait' || tc.status !== 'calling' || !tc.startedAt) return null
  const elapsed = formatElapsedMs(now.value - tc.startedAt)
  const cap = formatCapMs(tc.args?.block_until_ms)
  const runId = targetId(tc.args?.run_id)
  const jobRunId = targetId(tc.args?.job_run_id)
  let target = ''
  if (runId != null) target = ` for run #${runId}`
  else if (jobRunId != null) target = ` for job run #${jobRunId}`
  const capSuffix = cap ? ` / up to ${cap}` : ''
  return `Waiting${target}… ${elapsed}${capSuffix}`
}

function callApiCallingLabel(tc: ToolActivity): string | null {
  if (tc.name !== 'call_api' || tc.status !== 'calling' || !tc.startedAt) return null
  // Args are absent between tool_call_start and tool_call (input still being
  // generated) — fall back to the generic "API call..." label until they land.
  const path = typeof tc.args?.path === 'string' ? tc.args.path : ''
  if (!path) return null
  const method = String(tc.args?.method ?? 'GET').toUpperCase()
  const elapsed = formatElapsedMs(now.value - tc.startedAt)
  return `${method} ${path}… ${elapsed}`
}

function toolLabel(tc: ToolActivity) {
  if (tc.status === 'calling') {
    const waitLabel = waitCallingLabel(tc)
    if (waitLabel) return waitLabel
    const apiLabel = callApiCallingLabel(tc)
    if (apiLabel) return apiLabel
    const base = TOOL_LABELS[tc.name] || tc.name
    return `${base}...`
  }
  const base = TOOL_LABELS[tc.name] || tc.name
  if (tc.summary) return `${base} (${tc.summary})`
  return base
}

function formatToolValue(value: unknown) {
  if (typeof value === 'string') return value
  try { return JSON.stringify(value, null, 2) } catch { return String(value) }
}

function chatRefChipLabel(reference: ChatReference): string {
  const kind = reference.type === 'account'
    ? reference.integration ?? 'account'
    : reference.type.replace('_', ' ')
  return `${reference.label} · ${kind}`
}

function hasArgs(a: ToolActivity) {
  return !!a.args && Object.keys(a.args).length > 0
}

function hasToolDetail(a: ToolActivity) {
  return hasArgs(a) || a.result != null
}

function continueTurn() {
  void store.continueTurn(routeContext.value)
}
</script>

<template>
  <div ref="container" class="flex-1 overflow-y-auto min-h-0 min-w-0" @scroll="onScroll">
    <div v-if="!store.hasMessages" class="flex flex-col items-center justify-center h-full px-6 text-center">
      <div class="size-10 rounded-full bg-muted flex items-center justify-center mb-3">
        <Brain class="size-5 text-muted-foreground" />
      </div>
      <p class="text-sm font-medium text-foreground/80 mb-0.5">Envoy AI</p>
      <p class="text-xs text-muted-foreground leading-relaxed max-w-[240px]">
        Ask about tasks, runs, connectors, or operate the app via API tools.
      </p>
    </div>

    <div v-else class="space-y-3 p-3 min-w-0">
      <template v-for="msg in store.messages" :key="msg.id">
        <div v-if="msg.role === 'summary'" class="flex justify-center py-1">
          <Collapsible class="w-full max-w-[92%]">
            <CollapsibleTrigger class="flex w-full items-center gap-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors group cursor-pointer py-1">
              <div class="h-px flex-1 bg-border" />
              <span class="inline-flex items-center gap-1 shrink-0">
                <ChevronRight class="size-3 opacity-50 transition-transform group-data-[state=open]:rotate-90" />
                Earlier conversation summarized
              </span>
              <div class="h-px flex-1 bg-border" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div class="mt-1 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground whitespace-pre-wrap break-words leading-relaxed max-h-[240px] overflow-y-auto">
                {{ msg.text }}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div v-else-if="msg.role === 'continuation'" class="flex items-center gap-2 py-1 text-[11px] text-muted-foreground">
          <div class="h-px flex-1 bg-border" />
          <span class="shrink-0">Continued</span>
          <div class="h-px flex-1 bg-border" />
        </div>

        <div v-else-if="msg.role === 'user'" class="flex justify-end min-w-0">
          <div class="max-w-[85%] min-w-0 rounded-2xl rounded-br-md bg-primary text-primary-foreground px-3 py-2 text-[13px] leading-relaxed space-y-2 overflow-hidden">
            <p v-if="msg.text" class="whitespace-pre-wrap break-words">{{ msg.text }}</p>
            <div v-if="msg.attachments?.length" class="flex flex-wrap gap-1.5 justify-end">
              <template v-for="att in msg.attachments" :key="att.id">
                <a
                  v-if="att.kind === 'image'"
                  :href="aiApi.attachmentUrl(att.id)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="block max-w-full"
                >
                  <img
                    :src="aiApi.attachmentUrl(att.id)"
                    :alt="att.filename"
                    loading="lazy"
                    class="max-h-40 max-w-full rounded-md border border-primary-foreground/20"
                  >
                </a>
                <a
                  v-else
                  :href="aiApi.attachmentUrl(att.id)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1.5 rounded-md bg-primary-foreground/10 px-2 py-1 text-[11px] hover:bg-primary-foreground/20"
                >
                  <FileText class="size-3 shrink-0" />
                  <span class="max-w-[140px] truncate">{{ att.filename }}</span>
                  <span v-if="att.size" class="opacity-70">{{ formatAttachmentSize(att.size) }}</span>
                </a>
              </template>
            </div>
            <div v-if="msg.references?.length" class="flex flex-wrap gap-1.5 justify-end">
              <template v-for="reference in msg.references" :key="`${reference.type}:${reference.id}`">
                <RouterLink
                  v-if="resourceRefHasRoute(reference)"
                  :to="resourceRefToPath(reference)"
                  class="inline-flex items-center gap-1.5 rounded-md bg-primary-foreground/10 px-2 py-1 text-[11px] hover:bg-primary-foreground/20"
                >
                  <AtSign class="size-3 shrink-0" />
                  <span class="max-w-[140px] truncate">{{ chatRefChipLabel(reference) }}</span>
                </RouterLink>
                <span
                  v-else
                  class="inline-flex items-center gap-1.5 rounded-md bg-primary-foreground/10 px-2 py-1 text-[11px]"
                >
                  <AtSign class="size-3 shrink-0" />
                  <span class="max-w-[140px] truncate">{{ chatRefChipLabel(reference) }}</span>
                </span>
              </template>
            </div>
          </div>
        </div>

        <div v-else class="space-y-1.5 min-w-0">
          <Collapsible v-if="msg.thinking" :default-open="store.isStreaming && msg === store.messages[store.messages.length - 1]">
            <CollapsibleTrigger class="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors group cursor-pointer py-0.5">
              <ChevronRight class="size-3 opacity-50 transition-transform group-data-[state=open]:rotate-90" />
              <Brain class="size-3" />
              <span v-if="msg.thinkingDurationMs">Thought for {{ formatThinkingDuration(msg.thinkingDurationMs) }}</span>
              <span v-else class="flex items-center gap-1">Thinking…</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div class="mt-1 pl-[18px] text-[11px] text-muted-foreground/70 italic whitespace-pre-wrap break-words leading-relaxed border-l border-border ml-1.5 max-h-[200px] overflow-y-auto">
                {{ msg.thinking }}
              </div>
            </CollapsibleContent>
          </Collapsible>

          <template v-if="msg.segments?.length">
            <template v-for="(seg, si) in msg.segments as MessageSegment[]" :key="segmentKey(msg, si, seg)">
              <div
                v-if="seg.type === 'text' && segmentHasVisibleText(seg.text)"
                class="ai-prose min-w-0 text-[13px] leading-relaxed text-foreground/90"
                v-html="renderMarkdown(seg.text)"
                @click="onMarkdownClick"
              />
              <Collapsible v-else-if="seg.type === 'tool_call'">
                <CollapsibleTrigger class="flex items-center gap-1.5 min-w-0 max-w-full text-[11px] text-muted-foreground hover:text-foreground transition-colors group cursor-pointer py-0.5">
                  <ChevronRight class="size-3 shrink-0 opacity-50 transition-transform group-data-[state=open]:rotate-90" />
                  <Loader2 v-if="seg.activity.status === 'calling'" class="size-3 shrink-0 animate-spin" />
                  <component v-else :is="TOOL_ICONS[seg.activity.name] || Wrench" class="size-3 shrink-0" />
                  <span class="min-w-0 truncate" :title="toolLabel(seg.activity)">{{ toolLabel(seg.activity) }}</span>
                </CollapsibleTrigger>
                <CollapsibleContent v-if="hasToolDetail(seg.activity)">
                  <div class="mt-1 ml-1.5 pl-[18px] border-l border-border space-y-2 min-w-0">
                    <div v-if="hasArgs(seg.activity)">
                      <div class="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50 mb-0.5">Request</div>
                      <pre class="text-[11px] text-muted-foreground/70 whitespace-pre-wrap break-words leading-relaxed max-h-[200px] overflow-y-auto font-mono">{{ formatToolValue(seg.activity.args) }}</pre>
                    </div>
                    <div v-if="seg.activity.result != null">
                      <div class="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/50 mb-0.5">Response</div>
                      <pre class="text-[11px] text-muted-foreground/70 whitespace-pre-wrap break-words leading-relaxed max-h-[200px] overflow-y-auto font-mono">{{ formatToolValue(seg.activity.result) }}</pre>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <div v-else-if="seg.type === 'resource'" class="flex flex-wrap gap-1.5 pt-0.5 min-w-0">
                <HoverCard v-for="ref in dedupeResourceRefs(seg.refs)" :key="`${ref.type}-${ref.id}`">
                  <RouterLink
                    v-if="resourceRefHasRoute(ref)"
                    :to="resourceRefToPath(ref)"
                    class="inline-flex max-w-full min-w-0"
                  >
                    <Badge variant="secondary" class="max-w-full text-[11px] font-normal cursor-pointer hover:bg-secondary/80 transition-colors">
                      <span class="truncate">{{ resourceRefLabel(ref) }}</span>
                    </Badge>
                  </RouterLink>
                  <Badge v-else variant="secondary" class="max-w-full text-[11px] font-normal">
                    <span class="truncate">{{ resourceRefLabel(ref) }}</span>
                  </Badge>
                  <template #content>
                    <ResourcePreview :resource-ref="ref" />
                  </template>
                </HoverCard>
              </div>
            </template>
            <div v-if="yamlBlocksForMessage(msg).length && store.editorActive" class="pt-0.5">
              <YamlDiffPreview
                :original-yaml="store.taskEditorOriginalYaml"
                :modified-yaml="store.taskEditorPendingYaml || yamlBlocksForMessage(msg).at(-1)"
              />
            </div>
            <div v-if="yamlStatusLabel(msg)" class="flex items-center gap-1.5 pt-0.5">
              <GitCompareArrows v-if="store.taskEditorPhase === 'review'" class="size-3 text-amber-600 dark:text-amber-400" />
              <Check v-else-if="store.taskEditorPhase === 'edit'" class="size-3 text-emerald-600 dark:text-emerald-400" />
              <Loader2 v-else class="size-3 animate-spin text-muted-foreground" />
              <span class="text-[11px] text-muted-foreground">{{ yamlStatusLabel(msg) }}</span>
            </div>
          </template>

          <div v-else-if="msg.text" class="space-y-1.5">
            <div class="ai-prose min-w-0 text-[13px] leading-relaxed text-foreground/90" v-html="renderMarkdown(msg.text)" @click="onMarkdownClick" />
          </div>

          <div
            v-if="msg.warningMessage"
            class="flex items-start gap-2 rounded-lg border bg-muted/30 px-2.5 py-2 min-w-0"
          >
            <AlertTriangle class="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <p class="min-w-0 text-[12px] text-muted-foreground leading-relaxed break-words">
              {{ msg.warningMessage }}
            </p>
          </div>

          <div
            v-if="msg.recoveryStatus"
            class="flex items-start gap-2 rounded-lg border bg-muted/30 px-2.5 py-2 min-w-0"
          >
            <AlertTriangle class="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
            <div class="min-w-0 flex-1 space-y-1.5">
              <p class="text-[12px] text-foreground/90 leading-relaxed break-words">
                {{ msg.recoveryStatus.message }}
              </p>
              <p
                v-if="msg.recoveryStatus.serverMessage"
                class="text-[11px] text-muted-foreground leading-relaxed break-words"
              >
                {{ msg.recoveryStatus.serverMessage }}
              </p>
              <Button
                v-if="msg.recoveryStatus.continuable && msg === store.latestAssistantMessage"
                variant="outline"
                size="sm"
                class="h-7 gap-1.5 text-xs"
                :disabled="store.isStreaming"
                @click="continueTurn"
              >
                <Play class="size-3" />
                Continue
              </Button>
              <Button
                v-else-if="msg === store.latestAssistantMessage && store.canRetry"
                variant="outline"
                size="sm"
                class="h-7 gap-1.5 text-xs"
                :disabled="store.isStreaming"
                @click="store.retryLast()"
              >
                <RotateCcw class="size-3" />
                Retry
              </Button>
            </div>
          </div>

          <div
            v-else-if="msg.errorMessage"
            class="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-2.5 py-2 min-w-0"
          >
            <AlertTriangle class="size-3.5 text-destructive shrink-0 mt-0.5" />
            <div class="min-w-0 flex-1 space-y-1.5">
              <p class="text-[12px] text-destructive leading-relaxed break-words">{{ msg.errorMessage }}</p>
              <Button
                v-if="msg === store.latestAssistantMessage && store.canRetry"
                variant="outline"
                size="sm"
                class="h-7 gap-1.5 text-xs"
                :disabled="store.isStreaming"
                @click="store.retryLast()"
              >
                <RotateCcw class="size-3" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="store.compactionActive"
        class="flex items-center gap-1.5 pl-1 pt-0.5 text-[11px] text-muted-foreground"
        role="status"
        aria-label="Summarizing earlier conversation"
      >
        <Loader2 class="size-3 animate-spin" />
        <span>Summarizing earlier conversation…</span>
      </div>

      <div v-if="showTypingIndicator" class="flex items-center gap-1 pl-1 pt-0.5" role="status" aria-label="Assistant is responding">
        <span class="typing-dot" />
        <span class="typing-dot" />
        <span class="typing-dot" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Typing indicator — three pulsing dots while the AI is working ──── */
.typing-dot {
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background-color: var(--muted-foreground);
  opacity: 0.35;
  animation: ai-typing 1.2s infinite ease-in-out;
}
.typing-dot:nth-child(2) { animation-delay: 0.18s; }
.typing-dot:nth-child(3) { animation-delay: 0.36s; }
@keyframes ai-typing {
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 0.9; transform: translateY(-2px); }
}
@media (prefers-reduced-motion: reduce) {
  .typing-dot { animation-duration: 2.4s; transform: none; }
  @keyframes ai-typing { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.85; } }
}

/* ── Markdown prose — one consistent vertical rhythm ──────────── */
/* Every flow block shares the same bottom margin; margin-collapse
   then produces predictable, even spacing throughout the message. */
.ai-prose {
  overflow-wrap: anywhere;
  word-break: break-word;
}
.ai-prose :deep(p),
.ai-prose :deep(ul),
.ai-prose :deep(ol),
.ai-prose :deep(pre),
.ai-prose :deep(blockquote),
.ai-prose :deep(table) {
  margin: 0 0 0.7em;
}
.ai-prose :deep(> :first-child) { margin-top: 0; }
.ai-prose :deep(> :last-child) { margin-bottom: 0; }

/* Headings — subtle scale, more space above to group with content */
.ai-prose :deep(h1),
.ai-prose :deep(h2),
.ai-prose :deep(h3),
.ai-prose :deep(h4),
.ai-prose :deep(h5),
.ai-prose :deep(h6) {
  margin: 1.3em 0 0.5em;
  font-weight: 600;
  line-height: 1.3;
  color: var(--foreground);
}
.ai-prose :deep(h1) { font-size: 1.3em; letter-spacing: -0.01em; }
.ai-prose :deep(h2) { font-size: 1.18em; letter-spacing: -0.01em; }
.ai-prose :deep(h3) { font-size: 1.08em; }
.ai-prose :deep(h4),
.ai-prose :deep(h5),
.ai-prose :deep(h6) { font-size: 1em; }

/* Inline emphasis */
.ai-prose :deep(strong) { font-weight: 600; color: var(--foreground); }
.ai-prose :deep(em) { font-style: italic; }

/* Lists — modest indent for the narrow panel, even item spacing */
.ai-prose :deep(ul),
.ai-prose :deep(ol) { padding-left: 1.4em; }
.ai-prose :deep(li) { margin: 0.2em 0; }
.ai-prose :deep(li::marker) { color: var(--muted-foreground); }
.ai-prose :deep(li > ul),
.ai-prose :deep(li > ol) { margin: 0.2em 0 0; }
.ai-prose :deep(li > p) { margin-bottom: 0.35em; }

/* Horizontal rule — section divider */
.ai-prose :deep(hr) {
  margin: 1.15em 0;
  border: none;
  border-top: 1px solid var(--border);
}

/* Tables */
.ai-prose :deep(table) {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 0.92em;
  line-height: 1.35;
}
.ai-prose :deep(th),
.ai-prose :deep(td) {
  border: 1px solid var(--border);
  padding: 0.32em 0.6em;
  text-align: left;
  vertical-align: top;
}
.ai-prose :deep(th) {
  font-weight: 600;
  background: var(--muted);
  white-space: nowrap;
}
.ai-prose :deep(tbody tr:nth-child(even)) {
  background: color-mix(in srgb, var(--muted) 40%, transparent);
}

/* Inline code */
.ai-prose :deep(code) {
  font-size: 0.85em;
  padding: 0.12em 0.35em;
  border-radius: 0.3rem;
  background: var(--muted);
  overflow-wrap: break-word;
}

/* Code blocks */
.ai-prose :deep(pre) {
  padding: 0.65em 0.8em;
  border: 1px solid var(--border);
  border-radius: 0.45rem;
  background: var(--muted);
  overflow-x: auto;
  font-size: 0.85em;
  line-height: 1.45;
}
.ai-prose :deep(pre code) {
  padding: 0;
  border-radius: 0;
  background: transparent;
  font-size: inherit;
  white-space: pre;
}

/* Blockquote */
.ai-prose :deep(blockquote) {
  padding-left: 0.85em;
  border-left: 2px solid var(--border);
  color: var(--muted-foreground);
}

/* Links */
.ai-prose :deep(a) {
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

.ai-prose :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
